import { KeyIcon } from '@heroicons/react/24/outline';
import { Column } from '@tanstack/react-table';
import { memo } from 'react';

import { Icon } from '@/components/core/Icon';
import { Tooltip } from '@/components/core/Tooltip';
import { useTranslation } from '@/hooks/useTranslation';
import { logger } from '@/services/logger';
import { getAttributeDescription } from '@/services/utils';
import { DataModelAttribute } from '@/types/DataModel';

import { useAttributeLabelTranslator } from '../hooks/useAttributeLabelTranslator';
import { useIsAttributeReadonly } from '../hooks/useIsAttributeReadonly';

interface HeadProps<TData> {
    column: Column<TData>;
    isReadonlyByParent: boolean;
}

const CoreHead = <TData,>({ column, isReadonlyByParent = false }: HeadProps<TData>) => {
    const { t } = useTranslation();
    const { translateAttributeLabel } = useAttributeLabelTranslator();
    const attributeLabel = translateAttributeLabel(column.columnDef.meta?.attribute as unknown as DataModelAttribute);
    const tooltip = column.columnDef.meta?.attribute?.description || column.columnDef.meta?.attribute && getAttributeDescription(column.columnDef.meta?.attribute, t);
    const attribute = column.columnDef.meta?.attribute as unknown as DataModelAttribute;
    const readOnly  = useIsAttributeReadonly(attribute, attribute?.name, isReadonlyByParent);
    const showTooltip = tooltip && !readOnly;

    if (!column.columnDef.meta?.attribute) {
        logger.error('Head: Column meta attributes are missing');
        return null;
    }

    return (
        <div className="tw-flex tw-w-full tw-items-center">
            <div className="tw-flex tw-items-center tw-gap-1">
                <p className="tw-m-0">{attributeLabel}</p>
                <div className="tw-flex tw-flex-shrink-0 tw-items-center tw-justify-center">
                    {showTooltip && <Tooltip popupContent={tooltip} size="xs" />}
                </div>
            </div>
            {
                column.columnDef.meta?.isKeyMember &&
                <span className="tw-ml-auto tw-mr-1" title="Key Attribute">
                    <Icon icon={KeyIcon} size='xs' className="tw-text-gray-400 tw-text-xs" />
                </span>
            }
        </div>
    );
};

const Head = memo(CoreHead);

export { Head };
